// Service Worker registration. Called once from main.tsx; gated on
// `navigator.serviceWorker` availability and a secure context (the
// SW spec requires HTTPS or localhost).
//
// We register early so the first paint of a controlled client gets
// the activated SW; `clients.claim()` inside the SW's activate
// handler takes care of the first-visit case.

interface RegistrationState {
  waiting: boolean;
  active: boolean;
  version: string | null;
  /**
   * True when the controlling SW reports a PROTOCOL_VERSION that
   * does not match the page bundle's. The page MUST reload — the
   * SW is on a newer wire format and any envelope it intercepts
   * will be silently rewritten / rejected. UpdateBanner surfaces
   * this as a non-dismissable variant of the update prompt.
   */
  protocolSkew: boolean;
}

type Listener = (state: RegistrationState) => void;

const listeners = new Set<Listener>();
let last: RegistrationState = {
  waiting: false,
  active: false,
  version: null,
  protocolSkew: false,
};

function emit(state: RegistrationState): void {
  last = state;
  for (const l of listeners) {
    try {
      l(state);
    } catch {
      // listener errors must not break the propagation
    }
  }
}

export function onRegistrationChange(l: Listener): () => void {
  listeners.add(l);
  l(last);
  return () => listeners.delete(l);
}

export function currentRegistrationState(): RegistrationState {
  return last;
}

export async function registerServiceWorker(opts: {
  swUrl: URL | string;
  scope?: string;
  /**
   * Page bundle's wire protocol version. Compared against the
   * version the active SW reports; a mismatch flips `protocolSkew`
   * in the registration state so the banner can force a reload.
   */
  bundleProtocolVersion: number;
  /**
   * Which mode the SW should operate in for this page:
   *   - 'broker'      → SW serves /api/* from the in-browser broker
   *                     (web-only / paired-tab via `?ipc=1`).
   *   - 'passthrough' → SW lets /api/* hit the network (controllerd
   *                     or Cloudflare DO is the source of truth).
   * Sent to the SW as `sw.set-mode` once a controller is attached.
   * The SW persists the choice in Cache so subsequent reloads don't
   * race the message.
   */
  mode: 'broker' | 'passthrough';
}): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  const checkSkew = (swProtocolVersion: number | undefined): boolean =>
    typeof swProtocolVersion === 'number' &&
    swProtocolVersion !== opts.bundleProtocolVersion;
  const tellMode = () => {
    const c = navigator.serviceWorker.controller;
    if (c) c.postMessage({ type: 'sw.set-mode', mode: opts.mode });
  };
  try {
    const reg = await navigator.serviceWorker.register(opts.swUrl as URL, {
      type: 'module',
      scope: opts.scope ?? '/',
    });
    // Wire change events. The state machine:
    //   updatefound -> installing.onstatechange -> 'installed' + reg.waiting
    //   that's when we surface the banner.
    if (reg.waiting) {
      emit({
        waiting: true,
        active: !!reg.active,
        version: null,
        protocolSkew: false,
      });
    }
    reg.addEventListener('updatefound', () => {
      const sw = reg.installing;
      if (!sw) return;
      sw.addEventListener('statechange', () => {
        if (sw.state === 'installed' && reg.waiting) {
          emit({
            waiting: true,
            active: !!reg.active,
            version: null,
            protocolSkew: false,
          });
        }
        if (sw.state === 'activated') {
          emit({
            waiting: false,
            active: true,
            version: null,
            protocolSkew: false,
          });
        }
      });
    });
    // The SW itself broadcasts 'sw.activated' once clients.claim()
    // takes effect. Use that as the canonical "version is live" signal,
    // and check for protocol-version skew on the same hop.
    navigator.serviceWorker.addEventListener('message', (ev) => {
      const d = ev.data as {
        type?: string;
        version?: string;
        protocolVersion?: number;
      };
      if (!d) return;
      if (d.type === 'sw.activated' || d.type === 'sw.handshake.response') {
        emit({
          waiting: false,
          active: true,
          version: d.version ?? null,
          protocolSkew: checkSkew(d.protocolVersion),
        });
      }
    });
    // Trigger a background update check on every boot so a fresh
    // bundle behind a long-lived tab is noticed without a hard
    // reload.
    void reg.update();
    // Tell the SW which mode to operate in. If a controller is
    // already attached we send immediately; otherwise we wait for
    // `controllerchange` (first install) and re-send. The SW
    // persists the answer in Cache so the next boot doesn't race.
    if (navigator.serviceWorker.controller) {
      tellMode();
    }
    navigator.serviceWorker.addEventListener('controllerchange', tellMode);
    // Ask the currently-controlling SW (if any) what wire version it
    // speaks. If it's newer than this bundle, we discover the skew
    // at boot rather than waiting for the next activation.
    const controller = navigator.serviceWorker.controller;
    if (controller) {
      controller.postMessage({ type: 'sw.handshake' });
    }
  } catch {
    // SW registration failure (CSP, file 404, etc.) is non-fatal —
    // the page still functions, just without offline / broker
    // intercept.
  }
}

/**
 * Send a `skip-waiting` message to the installed-but-waiting SW so
 * it activates immediately. The update banner calls this on user
 * confirmation; the SW activates and the page reloads after the
 * 'sw.activated' broadcast.
 */
export async function activateWaitingServiceWorker(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg || !reg.waiting) return;
  reg.waiting.postMessage({ type: 'sw.skip-waiting' });
}
